import { Grid, Paper } from "@mui/material";
import { useMemo } from "react";
import { MarginalAbatementCostModel } from "./data/MaginalAbatementResult";
import { generateTableColumn } from "./data/metricsUtils";
import CustomDataGrid from "pages/PortfolioView/ProjectRegister/CustomDataGrid";
import { Constants, TitleDefinitionConstants } from "constants/constant";
import {
  getProjectRegisterGroupHeader,
  getProjectRegisterRows,
  getProjectRegisterTotals,
  ProjectRegisterGroupBy,
} from "./data/projectRegisterTableUtils";
import { normalizeMeasureLabel } from "./data/measureLabelUtils";

const sortRowsByChartMeasureOrder = (
  rows: ReturnType<typeof getProjectRegisterRows>,
  chartMeasureOrder: string[] | undefined
) => {
  if (!Array.isArray(chartMeasureOrder) || chartMeasureOrder.length === 0) {
    return rows;
  }

  const orderMap = new Map<string, number>();

  chartMeasureOrder.forEach((label, index) => {
    const normalizedLabel = normalizeMeasureLabel(label);
    if (!normalizedLabel || orderMap.has(normalizedLabel)) {
      return;
    }
    orderMap.set(normalizedLabel, index);
  });

  if (orderMap.size === 0) {
    return rows;
  }

  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftOrder = orderMap.get(normalizeMeasureLabel(left.row.measureName));
      const rightOrder = orderMap.get(normalizeMeasureLabel(right.row.measureName));

      const safeLeftOrder = leftOrder ?? Number.MAX_SAFE_INTEGER;
      const safeRightOrder = rightOrder ?? Number.MAX_SAFE_INTEGER;

      if (safeLeftOrder !== safeRightOrder) {
        return safeLeftOrder - safeRightOrder;
      }

      return left.index - right.index;
    })
    .map((entry) => entry.row);
};

/**
 * ProjectRegisterTable component displays a data grid of marginal abatement costs.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {MarginalAbatementCostModel[]} props.marginalAbatementCost - Array of marginal abatement cost data.
 * @returns {React.FC} The Project Register Table component.
 */
const ProjectRegisterTable: React.FC<{
  marginalAbatementCost: MarginalAbatementCostModel[];
  currency: string;
  groupBy: ProjectRegisterGroupBy;
  chartMeasureOrder?: string[];
}> = ({ marginalAbatementCost, currency, groupBy, chartMeasureOrder }) => {
  const UNIT_TCO2E = Constants.UNIT_TCO2E;
  const showDetailColumns = groupBy === "Name";

  const rows = useMemo(
    () =>
      sortRowsByChartMeasureOrder(
        getProjectRegisterRows(marginalAbatementCost, groupBy),
        chartMeasureOrder
      ),
    [marginalAbatementCost, groupBy, chartMeasureOrder]
  );

  const groupHeader = getProjectRegisterGroupHeader(groupBy);

  const columns = [
    ...(showDetailColumns
      ? [
          {
            field: "measureNumber",
            headerClassName: "table-header",
            headerName: "Measure ID",
            minWidth: 40,
          },
        ]
      : []),
    {
      field: "measureName",
      headerClassName: "table-header",
      headerName: groupHeader,
      minWidth: 160,
    },
    ...(showDetailColumns
      ? [
          {
            field: "businessUnit",
            headerClassName: "table-header",
            headerName: "Business Unit",
          },
          {
            field: "startYear",
            headerClassName: "table-header",
            headerName: "Start Year",
          },
          {
            field: "execution",
            headerClassName: "table-header",
            headerName: "Execution",
          },
          {
            field: "pace",
            headerClassName: "table-header",
            headerName: "Pace",
          },
          {
            field: "endYear",
            headerClassName: "table-header",
            headerName: "End Year",
          },
        ]
      : []),
    generateTableColumn(
      "emissionReduction",
      `Emissions Reduction (${UNIT_TCO2E})`,
      208
    ),
    ...(showDetailColumns
      ? [generateTableColumn("financialLifetime", "Financial Lifetime", 208)]
      : []),
    generateTableColumn("averageAnnualROI", "Average Annual ROI (%)", 208),
    generateTableColumn(
      "annualNPV",
      `Annual NPV (${currency})`,
      160,
      TitleDefinitionConstants.annualNPVTooltip
    ),
    generateTableColumn(
      "capExInvestment",
      `CapEx Investment (${currency})`,
      160,
      ""
    ),
    generateTableColumn(
      "mac",
      `MAC(${currency}/${UNIT_TCO2E})`,
      140,
      TitleDefinitionConstants.macTooltip
    ),
  ];

  const totalRow = useMemo(
    () => ({
      ...getProjectRegisterTotals(rows),
      measureName: "Total",
      isTotalRow: true,
    }),
    [rows]
  );

  return (
    <Grid item xs={12} sx={{ mb: 1 }}>
      <Paper variant="outlined">
        <Grid item sx={{ ml: 1, mt: 1, mb: 4 }}>
          <div
            style={{
              fontFamily: "Inter",
              fontSize: "18px",
              fontWeight: "600",
              color: "#05004E",
              width: "100%",
              marginLeft: "8px",
            }}
          >
            Project Register
          </div>
        </Grid>
        <Grid item>
          <CustomDataGrid
            rows={rows}
            totalRow={totalRow}
            columns={columns}
            getRowId={(row) =>
              row?.isTotalRow
                ? "project-register-total-row"
                : `${row?.reductionMeasureId}-${row?.measureName}`
            }
          />
        </Grid>
      </Paper>
    </Grid>
  );
};

export default ProjectRegisterTable;